// ============================================================
//  Contractor Rob Flooring — SubmitRFQPage.js  (Blue Cart)
//  Wholesale / New Customer Order Flow — v2.5
//  Changes: Full mobile responsive pass — single-column layout,
//           responsive step indicator, sidebar moves below form,
//           all grids collapse to 1fr on mobile.
// ============================================================

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBlueCart } from '../context/BlueCartContext';
import { submitRFQ } from '../services/api';
import {
  Trash2, ArrowRight, Eye, Printer, X,
  CheckCircle, Upload, ShieldCheck, Paperclip, Send, FileText,
} from 'lucide-react';

function getMOQDisplay(totalThicknessMm) {
  const map = { 5.0: 30000, 5.5: 30000, 6.0: 25000, 6.5: 25000 };
  return map[totalThicknessMm] ? map[totalThicknessMm].toLocaleString() : null;
}

const SWATCH = {
  'Southern Oak':  '#d4b896',
  'Maple Forest':  '#c8b89a',
  'Walnut Grove':  '#8b6c4f',
  'Earth Stone':   '#a09080',
  'EIR Collection':'#b8a898',
  'EIR':           '#b8a898',
};

function genRFQNumber() {
  const d = new Date();
  return `RFQ-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}-${String(Math.floor(Math.random()*9000)+1000)}`;
}

const digitsOnly = (v) => v.replace(/\D/g, '');

function formatPhone(raw) {
  const d = digitsOnly(raw).slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3)  return `(${d}`;
  if (d.length <= 6)  return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
}

function formatEIN(raw) {
  const d = digitsOnly(raw).slice(0, 9);
  if (d.length <= 2) return d;
  return `${d.slice(0,2)}-${d.slice(2)}`;
}

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

// Responsive two-column grid — collapses to 1 col on mobile via CSS class
const ERR = { fontFamily:'var(--sans)', fontSize:12, color:'#C84040', marginTop:4 };
const inputStyle = (hasErr) => ({ width:'100%', borderColor:hasErr?'#C84040':undefined, outlineColor:hasErr?'#C84040':undefined });

// ── RFQ Steps indicator — scrollable on mobile ──────────────
function RFQSteps({ step }) {
  const labels = ['Review items', 'Company info', 'Documents', 'Review & submit'];
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      marginTop: 32,
      overflowX: 'auto',
      scrollbarWidth: 'none',
      paddingBottom: 4,
      maxWidth: '100%',
    }}>
      {labels.map((label, i) => {
        const n = i + 1;
        const isDone = step > n, isActive = step === n;
        return (
          <React.Fragment key={label}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              opacity: (!isActive && !isDone) ? 0.4 : 1, flexShrink: 0,
            }}>
              <span style={{
                width: 26, height: 26, borderRadius: '50%',
                background: isDone ? 'var(--saffron-deep)' : isActive ? 'var(--indigo)' : 'var(--rule)',
                color: (isDone || isActive) ? '#fff' : 'var(--ash)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)',
                transition: 'background 0.2s', flexShrink: 0,
              }}>
                {isDone ? <CheckCircle size={13} strokeWidth={2.5}/> : n}
              </span>
              <span style={{
                fontFamily: 'var(--sans)', fontSize: 10,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                color: isActive ? 'var(--ink)' : 'var(--ash)',
                fontWeight: isActive ? 600 : 400, whiteSpace: 'nowrap',
              }}>{label}</span>
            </div>
            {n < labels.length && (
              <span style={{
                flex: '1 1 12px', height: 1, margin: '0 6px',
                background: isDone ? 'var(--saffron-deep)' : 'var(--rule)',
                minWidth: 10, transition: 'background 0.2s',
              }}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function FieldWrap({ children, span }) {
  return <div className={span === 'full' ? 'rfq-field-full' : ''}>{children}</div>;
}

function CRField({ label, name, type='text', value, onChange, required, placeholder, hint, span, error, maxLength, inputMode }) {
  return (
    <FieldWrap span={span}>
      <label className="cr-label" style={{ display:'block', marginBottom:5 }}>
        {label}{required && <span style={{ color:'var(--saffron-deep)', marginLeft:3 }}>*</span>}
      </label>
      {hint && <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:5, lineHeight:1.4 }}>{hint}</p>}
      <input type={type} name={name} value={value} onChange={e => onChange(e.target.value)} required={required} placeholder={placeholder} maxLength={maxLength} inputMode={inputMode} className="cr-input" style={inputStyle(!!error)}/>
      {error && <p style={ERR}>{error}</p>}
    </FieldWrap>
  );
}

function CRSelect({ label, name, value, onChange, required, hint, span, children, error }) {
  return (
    <FieldWrap span={span}>
      <label className="cr-label" style={{ display:'block', marginBottom:5 }}>
        {label}{required && <span style={{ color:'var(--saffron-deep)', marginLeft:3 }}>*</span>}
      </label>
      {hint && <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:5, lineHeight:1.4 }}>{hint}</p>}
      <select name={name} value={value} onChange={e => onChange(e.target.value)} required={required} className="cr-select" style={{ width:'100%', borderColor:error ? '#C84040' : undefined }}>{children}</select>
      {error && <p style={ERR}>{error}</p>}
    </FieldWrap>
  );
}

function PhoneField({ label, value, onChange, required, error, span }) {
  return (
    <FieldWrap span={span}>
      <label className="cr-label" style={{ display:'block', marginBottom:5 }}>{label}{required && <span style={{ color:'var(--saffron-deep)', marginLeft:3 }}>*</span>}</label>
      <input type="tel" value={value} inputMode="numeric" onChange={e => onChange(formatPhone(digitsOnly(e.target.value)))} placeholder="(313) 555-0100" maxLength={14} className="cr-input" style={inputStyle(!!error)}/>
      {error && <p style={ERR}>{error}</p>}
    </FieldWrap>
  );
}

function EINField({ value, onChange, required, error }) {
  return (
    <div>
      <label className="cr-label" style={{ display:'block', marginBottom:5 }}>Company EIN{required && <span style={{ color:'var(--saffron-deep)', marginLeft:3 }}>*</span>}</label>
      <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:5, lineHeight:1.4 }}>Federal Employer Identification Number</p>
      <input type="text" value={value} inputMode="numeric" onChange={e => onChange(formatEIN(e.target.value))} placeholder="12-3456789" maxLength={10} className="cr-input" style={inputStyle(!!error)}/>
      {error && <p style={ERR}>{error}</p>}
    </div>
  );
}

function EmailField({ label, value, onChange, required, error }) {
  return (
    <div>
      <label className="cr-label" style={{ display:'block', marginBottom:5 }}>{label}{required && <span style={{ color:'var(--saffron-deep)', marginLeft:3 }}>*</span>}</label>
      <input type="email" className="cr-input" required value={value} onChange={e => onChange(e.target.value)} placeholder="jane@acmeflooring.com" style={inputStyle(!!error)}/>
      {error && <p style={ERR}>{error}</p>}
    </div>
  );
}

function SectionHead({ title, subtitle }) {
  return (
    <div style={{ marginTop:32, marginBottom:16 }}>
      <p style={{ fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.22em', textTransform:'uppercase', color:'var(--saffron-deep)', fontWeight:600, marginBottom:4 }}>{title}</p>
      {subtitle && <p style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)' }}>{subtitle}</p>}
      <div style={{ height:1, background:'var(--rule)', marginTop:10 }}/>
    </div>
  );
}

function FileUploadField({ label, required, hint, file, onChange }) {
  const id = `fu-${label.replace(/\s+/g,'-').toLowerCase()}`;
  return (
    <div>
      <label className="cr-label" style={{ display:'block', marginBottom:5 }}>{label}{required && <span style={{ color:'var(--saffron-deep)', marginLeft:3 }}>*</span>}</label>
      {hint && <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:6, lineHeight:1.4 }}>{hint}</p>}
      <label htmlFor={id} style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '11px 14px',
        border: `1.5px dashed ${file ? 'var(--saffron-deep)' : 'var(--rule)'}`,
        borderRadius: 'var(--r-sm)',
        background: file ? 'rgba(232,179,57,.08)' : 'var(--ivory-soft)',
        cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
        // Prevent the label from overflowing on narrow viewports
        maxWidth: '100%', boxSizing: 'border-box',
      }}>
        {file
          ? <CheckCircle size={15} style={{ color:'var(--saffron-deep)', flexShrink:0 }}/>
          : <Upload size={15} style={{ color:'var(--ash)', flexShrink:0 }}/>}
        <span style={{
          fontFamily: 'var(--sans)', fontSize: 13,
          color: file ? 'var(--ink)' : 'var(--ash)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
          minWidth: 0,
        }}>{file ? file.name : 'Tap to upload — PDF, JPG, or PNG'}</span>
        {file && <span style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--ash)', flexShrink:0 }}>{(file.size/1024).toFixed(0)} KB</span>}
      </label>
      <input id={id} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:'none' }} onChange={e => onChange(e.target.files[0] || null)}/>
    </div>
  );
}

function validateCompanyInfo(form) {
  const e = {};
  if (!form.companyName.trim())    e.companyName    = 'Company name is required.';
  if (!form.businessType)          e.businessType   = 'Please select a business type.';
  if (!form.companyAddress.trim()) e.companyAddress = 'Company address is required.';
  const phoneDigits = digitsOnly(form.companyPhone);
  if (!phoneDigits)                e.companyPhone = 'Phone number is required.';
  else if (phoneDigits.length !== 10) e.companyPhone = 'Phone number must be 10 digits — (###) ###-####.';
  const einDigits = digitsOnly(form.companyEIN);
  if (!einDigits)                  e.companyEIN = 'EIN is required.';
  else if (einDigits.length !== 9) e.companyEIN = 'EIN must be 9 digits — ##-#######.';
  if (form.yearFounded) {
    const y = form.yearFounded.toString().replace(/\D/g,'');
    if (y.length !== 4) e.yearFounded = 'Please enter a valid 4-digit year.';
    else { const yr = parseInt(y,10); if (yr < 1800 || yr > new Date().getFullYear()) e.yearFounded = 'Please enter a realistic year.'; }
  }
  if (!form.companyStructure) e.companyStructure = 'Please select a company structure.';
  const isNonProfit = form.companyStructure === 'Non-Profit';
  const salesTaxIsNA = form.salesTaxCertNumber === 'N/A';
  if (!isNonProfit && !salesTaxIsNA && !form.salesTaxCertNumber.trim()) e.salesTaxCertNumber = 'Sales Tax Certificate Number is required.';
  if (!form.contactName.trim())  e.contactName  = 'Contact name is required.';
  if (!form.contactRole.trim())  e.contactRole  = 'Your role is required.';
  if (!form.contactEmail.trim())            e.contactEmail = 'Email address is required.';
  else if (!isValidEmail(form.contactEmail)) e.contactEmail = 'Please enter a valid email address.';
  if (!form.contactEmailConfirm.trim()) e.contactEmailConfirm = 'Please confirm your email address.';
  else if (form.contactEmail !== form.contactEmailConfirm) e.contactEmailConfirm = 'Email addresses do not match.';
  const cPhoneDigits = digitsOnly(form.contactPhone);
  if (!cPhoneDigits)               e.contactPhone = 'Phone number is required.';
  else if (cPhoneDigits.length !== 10) e.contactPhone = 'Phone number must be 10 digits — (###) ###-####.';
  if (!form.estimatedSF)   e.estimatedSF  = 'Please enter an estimated quantity.';
  if (!form.howDidYouHear) e.howDidYouHear = 'Please let us know how you heard about us.';
  return e;
}

function CompanyInfoStep({ form, setForm, onBack, onNext }) {
  const [errors, setErrors] = useState({});
  const isNonProfit = form.companyStructure === 'Non-Profit';
  const set = key => val => { setForm(f => ({...f, [key]:val})); if (errors[key]) setErrors(e => { const n={...e}; delete n[key]; return n; }); };
  const handlePhoneChange = field => formatted => { setForm(f => ({...f, [field]:formatted})); if (errors[field]) setErrors(e => { const n={...e}; delete n[field]; return n; }); };
  const handleNext = () => { const e = validateCompanyInfo(form); if (Object.keys(e).length) { setErrors(e); window.scrollTo({top:0,behavior:'smooth'}); return; } onNext(); };

  return (
    <div>
      <h2 style={{ fontFamily:'var(--serif)', fontSize:'clamp(22px,5vw,28px)', marginBottom:6 }}>New Customer Information</h2>
      <p style={{ fontFamily:'var(--sans)', fontSize:14, color:'var(--ash)', lineHeight:1.6 }}>Wholesale pricing is reserved for verified businesses. Complete the form below — our team will review your account within 1–2 business days.</p>

      <SectionHead title="Company Details" subtitle="Legal name and business classification"/>
      {/* Responsive two-column grid */}
      <div className="rfq-two-col">
        <CRField label="Company Name / DBA" name="companyName" required value={form.companyName} onChange={set('companyName')} placeholder="Acme Flooring LLC" error={errors.companyName}/>
        <CRSelect label="Type of Business" name="businessType" required value={form.businessType} onChange={set('businessType')} error={errors.businessType}>
          <option value="">Select type</option>
          <option>General Contractor</option><option>Flooring Installer / Sub-Contractor</option><option>Flooring Retailer</option>
          <option>Interior Designer / Architect</option><option>Property Manager / Developer</option><option>Residential Remodeler</option><option>Other</option>
        </CRSelect>
        <CRField label="Company Address" name="companyAddress" required span="full" value={form.companyAddress} onChange={set('companyAddress')} placeholder="123 Trade Street, Detroit, MI 48201" error={errors.companyAddress}/>
        <CRField label="Company Website" name="companyWebsite" type="url" value={form.companyWebsite} onChange={set('companyWebsite')} placeholder="https://acmeflooring.com"/>
        <PhoneField label="Company Phone Number" required value={form.companyPhone} onChange={handlePhoneChange('companyPhone')} error={errors.companyPhone}/>
        <EINField required value={form.companyEIN} onChange={set('companyEIN')} error={errors.companyEIN}/>
        <CRField label="Year Company Founded" name="yearFounded" type="text" inputMode="numeric" maxLength={4} value={form.yearFounded} onChange={v => set('yearFounded')(v.replace(/\D/g,'').slice(0,4))} placeholder="2010" error={errors.yearFounded} hint="Optional"/>
        <CRSelect label="Company Structure" name="companyStructure" required value={form.companyStructure} onChange={set('companyStructure')} error={errors.companyStructure}>
          <option value="">Select structure</option><option>LLC</option><option>Sole Proprietor</option><option>C Corp</option><option>S Corp</option><option>Non-Profit</option>
        </CRSelect>
        {!isNonProfit && (
          <div className="rfq-field-full">
            <label className="cr-label" style={{ display:'block', marginBottom:5 }}>
              Sales Tax Certificate Number (Reseller Permit)<span style={{ color:'var(--saffron-deep)', marginLeft:3 }}>*</span>
            </label>
            <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:8, lineHeight:1.4 }}>
              Required for tax-exempt wholesale purchasing. Select the option that applies to you.
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:12 }}>
              <label style={{ display:'flex', alignItems:'flex-start', gap:8, cursor:'pointer', padding:'10px 14px', borderRadius:'var(--r-sm)', border:`1.5px solid ${form.salesTaxCertNumber !== 'N/A' ? 'var(--indigo)' : 'var(--rule)'}`, background: form.salesTaxCertNumber !== 'N/A' ? 'rgba(59,95,122,.05)' : 'transparent', transition:'border-color 0.15s, background 0.15s' }}>
                <input type="radio" name="salesTaxCertOption" checked={form.salesTaxCertNumber !== 'N/A'} onChange={() => setForm(f => ({...f, salesTaxCertNumber:''}))} style={{ accentColor:'var(--indigo)', marginTop:2, flexShrink:0 }}/>
                <div>
                  <span style={{ fontFamily:'var(--sans)', fontSize:13, fontWeight:600, color:'var(--ink)' }}>I have a certificate number</span>
                  <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginTop:2, lineHeight:1.4 }}>Enter your state-issued reseller / sales tax exemption certificate number below.</p>
                </div>
              </label>
              <label style={{ display:'flex', alignItems:'flex-start', gap:8, cursor:'pointer', padding:'10px 14px', borderRadius:'var(--r-sm)', border:`1.5px solid ${form.salesTaxCertNumber === 'N/A' ? 'var(--indigo)' : 'var(--rule)'}`, background: form.salesTaxCertNumber === 'N/A' ? 'rgba(59,95,122,.05)' : 'transparent', transition:'border-color 0.15s, background 0.15s' }}>
                <input type="radio" name="salesTaxCertOption" checked={form.salesTaxCertNumber === 'N/A'} onChange={() => { setForm(f => ({...f, salesTaxCertNumber:'N/A'})); if (errors.salesTaxCertNumber) setErrors(e => { const n={...e}; delete n.salesTaxCertNumber; return n; }); }} style={{ accentColor:'var(--indigo)', marginTop:2, flexShrink:0 }}/>
                <div>
                  <span style={{ fontFamily:'var(--sans)', fontSize:13, fontWeight:600, color:'var(--ink)' }}>I don't have a sales tax certificate</span>
                  <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginTop:2, lineHeight:1.4 }}>Wholesale pricing will be available with standard applicable taxes included.</p>
                </div>
              </label>
            </div>
            {form.salesTaxCertNumber !== 'N/A' && (
              <input type="text" name="salesTaxCertNumber" value={form.salesTaxCertNumber} onChange={e => set('salesTaxCertNumber')(e.target.value)} placeholder="e.g. MI-1234567" className="cr-input" style={inputStyle(!!errors.salesTaxCertNumber)}/>
            )}
            {errors.salesTaxCertNumber && <p style={ERR}>{errors.salesTaxCertNumber}</p>}
          </div>
        )}
      </div>

      {isNonProfit && (
        <div style={{ marginTop:16, padding:'14px 18px', background:'rgba(232,179,57,.08)', borderRadius:'var(--r-sm)', border:'1px solid var(--saffron)' }}>
          <p style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ink-soft)', lineHeight:1.5 }}><strong>Non-Profit note:</strong> Sales Tax Certificate Number is not required. Upload your proof of non-profit status in the next step.</p>
        </div>
      )}

      <SectionHead title="Primary Contact" subtitle="The person who will manage this account"/>
      <div className="rfq-two-col">
        <CRField label="Contact Name" name="contactName" required value={form.contactName} onChange={set('contactName')} placeholder="Jane Smith" error={errors.contactName}/>
        <CRField label="Your Role in Company" name="contactRole" required value={form.contactRole} onChange={set('contactRole')} placeholder="Owner, Purchasing Manager…" error={errors.contactRole}/>
        <EmailField label="Your Email Address" required value={form.contactEmail} onChange={set('contactEmail')} error={errors.contactEmail}/>
        <EmailField label="Confirm Email Address" required value={form.contactEmailConfirm} onChange={set('contactEmailConfirm')} error={errors.contactEmailConfirm}/>
        <PhoneField label="Your Phone Number" required value={form.contactPhone} onChange={handlePhoneChange('contactPhone')} error={errors.contactPhone}/>
      </div>

      <SectionHead title="Order Intent" subtitle="Help us prepare your quote"/>
      <div className="rfq-two-col">
        <CRField label="Estimated Order Quantity (SF)" name="estimatedSF" type="number" required value={form.estimatedSF} onChange={set('estimatedSF')} placeholder="e.g. 5000" hint="Square footage you expect to order initially" error={errors.estimatedSF}/>
        <CRSelect label="How did you hear about us?" name="howDidYouHear" required value={form.howDidYouHear} onChange={set('howDidYouHear')} error={errors.howDidYouHear}>
          <option value="">Select one</option><option>Google / Search Engine</option><option>Social Media (Instagram, Facebook, LinkedIn)</option><option>Industry Trade Show</option>
          <option>Referral from another contractor</option><option>Referral from a supplier</option><option>Email / Newsletter</option><option>Other</option>
        </CRSelect>
      </div>

      {Object.keys(errors).length > 0 && (
        <div style={{ marginTop:20, padding:'12px 16px', background:'rgba(192,57,43,.05)', border:'1px solid rgba(192,57,43,.2)', borderRadius:'var(--r-sm)' }}>
          <p style={{ fontFamily:'var(--sans)', fontSize:13, color:'#c0392b' }}>Please fix the highlighted fields above before continuing.</p>
        </div>
      )}

      <div className="rfq-step-nav">
        <button type="button" onClick={onBack} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'var(--sans)', fontSize:13, color:'var(--ash)', display:'flex', alignItems:'center', gap:6 }}>← Back</button>
        <button type="button" onClick={handleNext} className="btn btn-saffron btn-lg rfq-cta-btn" style={{ display:'flex', alignItems:'center', gap:8 }}>
          Continue to Documents <ArrowRight size={14}/>
        </button>
      </div>
    </div>
  );
}

function DocumentsStep({ form, setForm, onBack, onNext }) {
  const isNonProfit = form.companyStructure === 'Non-Profit';
  const salesTaxNA = form.salesTaxCertNumber === 'N/A';
  const set = key => file => setForm(f => ({...f, [key]:file}));
  const canContinue = (salesTaxNA || form.salesTaxCertFile) && form.businessLicenseFile && (!isNonProfit || form.nonProfitProofFile);
  const handleNext = () => { if (canContinue) { window.scrollTo({top:0,behavior:'smooth'}); onNext(); } };

  return (
    <div>
      <h2 style={{ fontFamily:'var(--serif)', fontSize:'clamp(22px,5vw,28px)', marginBottom:6 }}>Upload Documents</h2>
      <p style={{ fontFamily:'var(--sans)', fontSize:14, color:'var(--ash)', lineHeight:1.6, marginBottom:24 }}>We verify every wholesale account before sharing pricing. Documents are handled securely and never shared.</p>

      <div style={{ display:'flex', alignItems:'flex-start', gap:10, padding:'12px 16px', background:'var(--ivory-soft)', borderRadius:'var(--r-sm)', border:'1px solid var(--rule)', marginBottom:28 }}>
        <ShieldCheck size={16} style={{ color:'var(--indigo)', flexShrink:0, marginTop:1 }}/>
        <p style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)', lineHeight:1.5 }}>Files are uploaded to secure cloud storage and reviewed manually by our team. Accepted formats: PDF, JPG, PNG — up to 10 MB each.</p>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:24 }}>
        {!salesTaxNA && (
          <FileUploadField label="Company Sales Tax Certificate" required file={form.salesTaxCertFile} onChange={set('salesTaxCertFile')} hint={isNonProfit ? 'Not required for non-profits.' : 'Your state-issued reseller / sales tax exemption certificate.'}/>
        )}
        <FileUploadField label="Company Business License" required file={form.businessLicenseFile} onChange={set('businessLicenseFile')} hint="Current, active business license issued by your city, county, or state."/>
        {isNonProfit && (
          <FileUploadField label="Proof of Non-Profit Status" required file={form.nonProfitProofFile} onChange={set('nonProfitProofFile')} hint="IRS determination letter (Form 1023 approval) or equivalent documentation."/>
        )}
      </div>

      <div className="rfq-step-nav">
        <button type="button" onClick={() => { window.scrollTo({top:0,behavior:'smooth'}); onBack(); }} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'var(--sans)', fontSize:13, color:'var(--ash)', display:'flex', alignItems:'center', gap:6 }}>← Back</button>
        <button type="button" onClick={handleNext} disabled={!canContinue} className="btn btn-saffron btn-lg rfq-cta-btn" style={{ display:'flex', alignItems:'center', gap:8, opacity:canContinue ? 1 : 0.4 }}>
          Review &amp; Submit <ArrowRight size={14}/>
        </button>
      </div>
    </div>
  );
}

function ReviewStep({ form, items, dims, submitting, error, onBack, onSubmit, onPreview }) {
  const isNonProfit = form.companyStructure === 'Non-Profit';
  const totalUnits  = items.reduce((a,b) => a + b.quantity, 0);

  const Row = ({ label, value }) => value ? (
    <div style={{ display:'grid', gridTemplateColumns:'minmax(120px,140px) 1fr', gap:'4px 12px', padding:'9px 0', borderBottom:'1px solid var(--rule)' }}>
      <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)' }}>{label}</span>
      <span style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ink)', wordBreak:'break-word' }}>{value}</span>
    </div>
  ) : null;

  return (
    <div>
      <h2 style={{ fontFamily:'var(--serif)', fontSize:'clamp(22px,5vw,28px)', marginBottom:6 }}>Final Review</h2>
      <p style={{ fontFamily:'var(--sans)', fontSize:14, color:'var(--ash)', lineHeight:1.6, marginBottom:24 }}>Confirm everything looks right before submitting. Our team will review your application and contact you within 1–2 business days.</p>

      <div className="cr-card" style={{ padding:16, marginBottom:14 }}>
        <p style={{ fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.22em', textTransform:'uppercase', color:'var(--saffron-deep)', fontWeight:600, marginBottom:12 }}>Company Information</p>
        <Row label="Company Name / DBA" value={form.companyName}/><Row label="Type of Business" value={form.businessType}/>
        <Row label="Address" value={form.companyAddress}/>
        {form.companyWebsite && <Row label="Website" value={form.companyWebsite}/>}
        <Row label="EIN" value={form.companyEIN}/><Row label="Phone" value={form.companyPhone}/>
        {form.yearFounded && <Row label="Year Founded" value={form.yearFounded}/>}
        <Row label="Structure" value={form.companyStructure}/>
        {!isNonProfit && <Row label="Sales Tax Cert #" value={form.salesTaxCertNumber}/>}
      </div>

      <div className="cr-card" style={{ padding:16, marginBottom:14 }}>
        <p style={{ fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.22em', textTransform:'uppercase', color:'var(--saffron-deep)', fontWeight:600, marginBottom:12 }}>Primary Contact</p>
        <Row label="Name" value={form.contactName}/><Row label="Role" value={form.contactRole}/>
        <Row label="Email" value={form.contactEmail}/><Row label="Phone" value={form.contactPhone}/>
        <Row label="Estimated SF" value={form.estimatedSF ? `${Number(form.estimatedSF).toLocaleString()} sq ft` : ''}/>
        <Row label="How you heard" value={form.howDidYouHear}/>
      </div>

      <div className="cr-card" style={{ padding:16, marginBottom:14 }}>
        <p style={{ fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.22em', textTransform:'uppercase', color:'var(--saffron-deep)', fontWeight:600, marginBottom:12 }}>Documents</p>
        {[{label:'Sales Tax Certificate',file:form.salesTaxCertFile},{label:'Business License',file:form.businessLicenseFile},{label:'Non-Profit Proof',file:form.nonProfitProofFile}].filter(d => d.file).map(d => (
          <div key={d.label} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 0', borderBottom:'1px solid var(--rule)', flexWrap:'wrap' }}>
            <Paperclip size={13} style={{ color:'var(--indigo)', flexShrink:0 }}/>
            <span style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)', flex:1, minWidth:80 }}>{d.label}</span>
            <span style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--ink)', wordBreak:'break-all' }}>{d.file.name}</span>
          </div>
        ))}
      </div>

      <div className="cr-card" style={{ padding:16, marginBottom:24 }}>
        <p style={{ fontFamily:'var(--sans)', fontSize:10, letterSpacing:'.22em', textTransform:'uppercase', color:'var(--saffron-deep)', fontWeight:600, marginBottom:12 }}>Selected Collections ({items.length} item{items.length !== 1 ? 's' : ''})</p>
        {items.map(item => {
          const pid = item.cartItemId, cfg = item.configuration || {};
          return (
            <div key={pid} style={{ display:'flex', alignItems:'flex-start', gap:12, padding:'10px 0', borderBottom:'1px solid var(--rule)' }}>
              <div style={{ width:40, height:40, borderRadius:'var(--r-sm)', flexShrink:0, background:SWATCH[item.product.name]||'#d4c4b0' }}/>
              <div style={{ flex:1, minWidth:0 }}>
                <p style={{ fontFamily:'var(--serif)', fontSize:16, color:'var(--ink)' }}>{item.product.name}</p>
                <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginTop:2 }}>Qty {item.quantity}</p>
                {cfg.s_number && <p style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--indigo)', marginTop:3 }}>S# {cfg.s_number}{cfg.plank_dimension ? ` · ${cfg.plank_dimension}` : ''}</p>}
                {cfg.plank_thickness_mm != null && <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginTop:1 }}>{cfg.plank_thickness_mm.toFixed(1)}mm plank{cfg.wear_layer_mm != null ? ` · ${cfg.wear_layer_mm.toFixed(1)}mm wear` : ''}{cfg.ixpe_layer_mm != null ? ` + ${cfg.ixpe_layer_mm.toFixed(1)}mm IXPE` : ''}</p>}
                {cfg.total_thickness_mm != null && <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ink-soft)', marginTop:1 }}>Total {cfg.total_thickness_mm}mm{getMOQDisplay(cfg.total_thickness_mm) ? ` · MOQ ${getMOQDisplay(cfg.total_thickness_mm)} sq ft` : ''}</p>}
                {cfg.order_quantity && <p style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--indigo)', marginTop:1 }}>Qty: {cfg.order_quantity}</p>}
              </div>
            </div>
          );
        })}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:14, marginTop:4 }}>
          <p style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)' }}>Total units requested</p>
          <p style={{ fontFamily:'var(--serif)', fontSize:22, color:'var(--ink)', fontWeight:600 }}>{totalUnits}</p>
        </div>
        <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginTop:6, fontStyle:'italic' }}>* Pricing is quote-based. Verified wholesale rates will be confirmed within 1–2 business days.</p>
      </div>

      {error && (
        <div style={{ background:'rgba(192,57,43,.06)', border:'1px solid rgba(192,57,43,.2)', borderRadius:'var(--r-sm)', padding:'12px 16px', marginBottom:16 }}>
          <p style={{ fontFamily:'var(--sans)', fontSize:13, color:'#c0392b' }}>{error}</p>
        </div>
      )}

      {/* Nav row — stacks on mobile */}
      <div className="rfq-step-nav">
        <button type="button" onClick={() => { window.scrollTo({top:0,behavior:'smooth'}); onBack(); }} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:'var(--sans)', fontSize:13, color:'var(--ash)', display:'flex', alignItems:'center', gap:6 }}>← Back</button>
        <div className="rfq-review-actions">
          <button type="button" onClick={onPreview} className="btn btn-outline" style={{ display:'flex', alignItems:'center', gap:8 }}><Eye size={14}/> Preview Quote</button>
          <button type="button" onClick={onSubmit} disabled={submitting} className="btn btn-saffron btn-lg rfq-cta-btn" style={{ display:'flex', alignItems:'center', gap:8 }}>
            {submitting ? 'Submitting…' : <><Send size={14}/> Submit Quote Request</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function InvoicePreview({ form, items, dims, rfqNum, onClose }) {
  const printRef   = useRef(null);
  const today      = new Date().toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'});
  const totalUnits = items.reduce((a,b) => a + b.quantity, 0);
  const handlePrint = () => {
    const w = window.open('','','width=900,height=700');
    w.document.write(`<html><head><title>${rfqNum}</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Helvetica Neue',Helvetica,sans-serif;background:#fff;color:#161512;-webkit-print-color-adjust:exact}@media print{body{margin:0}}</style></head><body>${printRef.current.innerHTML}</body></html>`);
    w.document.close(); w.focus(); setTimeout(() => { w.print(); w.close(); }, 400);
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:400, background:'rgba(22,21,18,.72)', backdropFilter:'blur(10px)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'16px 16px 40px', overflowY:'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:720, marginTop:8 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
          <span style={{ fontFamily:'var(--sans)', fontSize:11, letterSpacing:'.2em', textTransform:'uppercase', color:'rgba(255,255,255,.6)' }}>Quote Preview</span>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={handlePrint} style={{ display:'flex', alignItems:'center', gap:6, fontFamily:'var(--sans)', fontSize:11, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--ink)', background:'var(--saffron)', border:'none', padding:'8px 16px', borderRadius:'var(--r-sm)', cursor:'pointer' }}><Printer size={13}/> Print / Save PDF</button>
            <button onClick={onClose} style={{ width:36, height:36, borderRadius:'50%', background:'rgba(255,255,255,.15)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'none', cursor:'pointer' }}><X size={16}/></button>
          </div>
        </div>
        {/* Print-safe inner content — overflow scroll on small screens */}
        <div style={{ overflowX:'auto' }}>
          <div ref={printRef} style={{ background:'#fff', borderRadius:2, boxShadow:'0 40px 100px rgba(0,0,0,.5)', overflow:'hidden', minWidth:320 }}>
            <div style={{ height:4, background:'#161512' }}/>
            <div style={{ padding:'32px 28px' }}>
              <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'space-between', alignItems:'flex-start', gap:16, marginBottom:32 }}>
                <div>
                  <p style={{ fontFamily:'Georgia,serif', fontSize:20, color:'#161512' }}>Contractor Rob Flooring</p>
                  <p style={{ fontFamily:'sans-serif', fontSize:11, color:'#6E6A60', marginTop:6, lineHeight:1.7 }}>Love3 Products, Inc.<br/>900 Faulk Road, Suite 201<br/>Wilmington, DE 19803<br/>Info@contractorrob.com</p>
                </div>
                <div style={{ textAlign:'right' }}>
                  <p style={{ fontFamily:'sans-serif', fontSize:18, fontWeight:700, color:'#161512' }}>REQUEST FOR QUOTE</p>
                  <p style={{ fontFamily:'monospace', fontSize:13, color:'#C8941F', marginTop:4, letterSpacing:'.06em' }}>#{rfqNum}</p>
                  <p style={{ fontFamily:'sans-serif', fontSize:11, color:'#6E6A60', marginTop:8, lineHeight:1.7 }}>Date: {today}<br/>Response: 1–2 business days</p>
                </div>
              </div>
              <div style={{ height:1, background:'#DCD6C5', marginBottom:24 }}/>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px 32px', marginBottom:28 }}>
                <div>
                  <p style={{ fontFamily:'sans-serif', fontSize:9, letterSpacing:'.2em', textTransform:'uppercase', color:'#B5B0A4', marginBottom:8 }}>Customer</p>
                  <p style={{ fontFamily:'Georgia,serif', fontSize:16, color:'#161512' }}>{form.contactName}</p>
                  {form.contactRole && <p style={{ fontFamily:'sans-serif', fontSize:12, color:'#6E6A60', marginTop:3 }}>{form.contactRole}</p>}
                  {form.companyName && <p style={{ fontFamily:'sans-serif', fontSize:13, color:'#3B3833', marginTop:4, fontWeight:500 }}>{form.companyName}</p>}
                  <p style={{ fontFamily:'sans-serif', fontSize:13, color:'#6E6A60', marginTop:4 }}>{form.contactEmail}</p>
                  {form.contactPhone && <p style={{ fontFamily:'sans-serif', fontSize:13, color:'#6E6A60' }}>{form.contactPhone}</p>}
                </div>
                <div>
                  {form.companyStructure && <><p style={{ fontFamily:'sans-serif', fontSize:9, letterSpacing:'.2em', textTransform:'uppercase', color:'#B5B0A4', marginBottom:8 }}>Business Type</p><p style={{ fontFamily:'sans-serif', fontSize:13, color:'#3B3833', marginBottom:14 }}>{form.businessType} · {form.companyStructure}</p></>}
                  {form.estimatedSF && <><p style={{ fontFamily:'sans-serif', fontSize:9, letterSpacing:'.2em', textTransform:'uppercase', color:'#B5B0A4', marginBottom:8 }}>Estimated Order (SF)</p><p style={{ fontFamily:'sans-serif', fontSize:13, color:'#3B3833' }}>{Number(form.estimatedSF).toLocaleString()} sq ft</p></>}
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:24 }}>
                  <thead>
                    <tr style={{ borderBottom:'2px solid #161512' }}>
                      {['Collection','Configuration','Units','Notes'].map(h => (
                        <th key={h} style={{ fontFamily:'sans-serif', fontSize:9, letterSpacing:'.2em', textTransform:'uppercase', color:'#6E6A60', fontWeight:600, textAlign:'left', paddingBottom:10, paddingRight:10 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const cfg = item.configuration || {};
                      return (
                        <tr key={item.cartItemId} style={{ borderBottom:'1px solid #ECE6D6', background:idx%2===0?'#FBFAF6':'#fff' }}>
                          <td style={{ padding:'12px 10px 12px 0', fontFamily:'Georgia,serif', fontSize:14, color:'#161512' }}>{item.product.name}</td>
                          <td style={{ padding:'12px 10px', fontFamily:'monospace', fontSize:10, color:'#6E6A60', minWidth:100 }}>
                            {cfg.s_number && <div>S# {cfg.s_number}</div>}
                            {cfg.plank_dimension && <div>{cfg.plank_dimension}</div>}
                            {cfg.plank_thickness_mm != null && <div>{cfg.plank_thickness_mm.toFixed(1)}mm plank{cfg.wear_layer_mm != null ? ` / ${cfg.wear_layer_mm.toFixed(1)}mm wear` : ''}</div>}
                            {cfg.ixpe_layer_mm != null && <div>IXPE {cfg.ixpe_layer_mm.toFixed(1)}mm</div>}
                            {cfg.total_thickness_mm != null && <div style={{color:'#C8941F'}}>Total: {cfg.total_thickness_mm}mm</div>}
                            {cfg.order_quantity && <div style={{color:'#3B5F7A'}}>Qty: {cfg.order_quantity}</div>}
                          </td>
                          <td style={{ padding:'12px 10px', fontFamily:'monospace', fontSize:15, color:'#161512', fontWeight:700 }}>{item.quantity}</td>
                          <td style={{ padding:'12px 0 12px 10px', fontFamily:'sans-serif', fontSize:11, color:'#6E6A60' }}>{item.notes||'—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:28 }}>
                <div style={{ width:200 }}>
                  {[{label:'Total Units',val:String(totalUnits)},{label:'Collections',val:String(items.length)}].map(row => (
                    <div key={row.label} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid #ECE6D6' }}>
                      <span style={{ fontFamily:'sans-serif', fontSize:12, color:'#6E6A60' }}>{row.label}</span>
                      <span style={{ fontFamily:'monospace', fontSize:13, color:'#161512' }}>{row.val}</span>
                    </div>
                  ))}
                  <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', marginTop:4, borderTop:'2px solid #161512' }}>
                    <span style={{ fontFamily:'sans-serif', fontSize:11, color:'#6E6A60', letterSpacing:'.06em', textTransform:'uppercase' }}>Pricing</span>
                    <span style={{ fontFamily:'Georgia,serif', fontSize:14, color:'#C8941F', fontWeight:600 }}>Quote-Based</span>
                  </div>
                </div>
              </div>
              <div style={{ borderTop:'1px solid #DCD6C5', paddingTop:18 }}>
                <p style={{ fontFamily:'sans-serif', fontSize:11, color:'#B5B0A4', lineHeight:1.65 }}>This document is a Request for Quotation only and does not constitute a binding order. No prices have been committed. Verified wholesale pricing will be confirmed within 1–2 business days.</p>
                <p style={{ fontFamily:'Georgia,serif', fontStyle:'italic', fontSize:13, color:'#6E6A60', marginTop:12 }}>"We provide quality flooring — and that's a promise you can stand on."</p>
              </div>
            </div>
            <div style={{ height:4, background:'#E8B339' }}/>
          </div>
        </div>
      </div>
    </div>
  );
}

function RFQSidebar({ items, dims, updateDim, updateQuantity, updateNotes, removeItem }) {
  const totalUnits = items.reduce((a,b) => a + b.quantity, 0);
  return (
    <div className="cr-card rfq-sidebar" style={{ padding:'24px 20px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20 }}>
        <span style={{ width:8, height:8, borderRadius:'50%', background:'var(--indigo)', flexShrink:0 }}/>
        <h2 style={{ fontFamily:'var(--serif)', fontSize:20, color:'var(--ink)' }}>RFQ Summary</h2>
        {items.length > 0 && <span className="chip chip-gold" style={{ marginLeft:'auto' }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>}
      </div>

      {items.length === 0
        ? <p style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ash)' }}>No items yet. Go back to the catalog and add products.</p>
        : (
          <div style={{ display:'flex', flexDirection:'column' }}>
            {items.map(item => {
              const pid = item.cartItemId;
              return (
                <div key={pid} style={{ paddingBottom:20, marginBottom:20, borderBottom:'1px solid var(--rule)' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:12 }}>
                    <div style={{ width:40, height:40, flexShrink:0, borderRadius:'var(--r-sm)', background:SWATCH[item.product.name]||'#d4c4b0' }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ fontFamily:'var(--serif)', fontSize:16, color:'var(--ink)' }}>{item.product.name}</p>
                      {item.product.category && <p className="eyebrow" style={{ marginTop:3 }}>{item.product.category}</p>}
                    </div>
                    <button onClick={() => removeItem(pid)} style={{ color:'var(--mist)', padding:4, flexShrink:0, background:'none', border:'none', cursor:'pointer', transition:'color .2s' }} onMouseEnter={e=>e.currentTarget.style.color='#c0392b'} onMouseLeave={e=>e.currentTarget.style.color='var(--mist)'}><Trash2 size={13}/></button>
                  </div>

                  {item.configuration && (
                    <div style={{ marginBottom:12, padding:'10px 12px', background:'rgba(59,95,122,.04)', borderRadius:'var(--r-sm)', border:'1px solid rgba(59,95,122,.15)' }}>
                      {item.configuration.s_number && <p style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--ink)', marginBottom:3 }}>S# {item.configuration.s_number}</p>}
                      {item.configuration.plank_dimension && <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:2 }}>{item.configuration.plank_dimension}</p>}
                      {item.configuration.plank_thickness_mm != null && <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:2 }}>{item.configuration.plank_thickness_mm.toFixed(1)}mm plank{item.configuration.wear_layer_mm != null ? ` · ${item.configuration.wear_layer_mm.toFixed(1)}mm wear` : ''}</p>}
                      {item.configuration.ixpe_layer_mm != null && <p style={{ fontFamily:'var(--sans)', fontSize:11, color:'var(--ash)', marginBottom:2 }}>IXPE {item.configuration.ixpe_layer_mm.toFixed(1)}mm Underlayment</p>}
                      {item.configuration.order_quantity && <p style={{ fontFamily:'var(--mono)', fontSize:11, color:'var(--indigo)', marginBottom:2 }}>Qty: {item.configuration.order_quantity}</p>}
                      {item.configuration.total_thickness_mm != null && (() => {
                        const moq = getMOQDisplay(item.configuration.total_thickness_mm);
                        return (
                          <div style={{ display:'flex', gap:16, marginTop:6, paddingTop:6, borderTop:'1px solid rgba(59,95,122,.12)' }}>
                            <div>
                              <span style={{ fontFamily:'var(--sans)', fontSize:9, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--ash)' }}>Total Thickness</span>
                              <p style={{ fontFamily:'var(--serif)', fontSize:15, color:'var(--ink)', lineHeight:1.1, marginTop:1 }}>{item.configuration.total_thickness_mm}mm</p>
                            </div>
                            {moq && (
                              <div>
                                <span style={{ fontFamily:'var(--sans)', fontSize:9, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--ash)' }}>MOQ</span>
                                <p style={{ fontFamily:'var(--serif)', fontSize:15, color:'var(--ink)', lineHeight:1.1, marginTop:1 }}>{moq} sq ft</p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
                    <span className="eyebrow" style={{ width:64, flexShrink:0 }}>Orders</span>
                    <span style={{ fontFamily:'var(--mono)', fontSize:13, color:'var(--ink)' }}>{item.quantity}</span>
                  </div>

                  <textarea placeholder="Notes for this item (colour, finish, room name…)" value={item.notes||''} onChange={e => updateNotes(pid, e.target.value)} rows={2} className="cr-textarea" style={{ minHeight:52, fontSize:12 }}/>
                </div>
              );
            })}
          </div>
        )
      }

      {items.length > 0 && (
        <div style={{ borderTop:'1px solid var(--rule)', paddingTop:16 }}>
          <dl style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:'6px 16px', alignItems:'baseline' }}>
            <dt style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)' }}>Total orders</dt>
            <dd style={{ fontFamily:'var(--mono)', fontSize:14, color:'var(--ink)', textAlign:'right' }}>{totalUnits}</dd>
            <dt style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ash)' }}>Collections</dt>
            <dd style={{ fontFamily:'var(--mono)', fontSize:14, color:'var(--ink)', textAlign:'right' }}>{items.length} item{items.length !== 1 ? 's' : ''}</dd>
          </dl>
          <div style={{ marginTop:14, padding:'12px 14px', background:'rgba(59,95,122,.05)', borderRadius:'var(--r-sm)', border:'1px solid rgba(59,95,122,.2)' }}>
            <p style={{ fontFamily:'var(--sans)', fontSize:12, color:'var(--ink-soft)', lineHeight:1.5 }}>Wholesale pricing is quote-based. Our team will review your selections and respond within 1–2 business days.</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SubmitRFQPage() {
  const { items, removeItem, updateNotes, updateQuantity, clearCart } = useBlueCart();
  const navigate = useNavigate();
  const [step, setStep]               = useState(1);
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const rfqNum = useRef(genRFQNumber()).current;
  const [dims, setDims] = useState(() => Object.fromEntries(items.map(i => [i.cartItemId, {width:'',length:'',unit:'sqft'}])));
  const updateDim = (id, field, val) => setDims(prev => ({...prev, [id]:{...(prev[id]||{}), [field]:val}}));
  const [form, setForm] = useState({
    companyName:'', businessType:'', companyAddress:'', companyWebsite:'', companyEIN:'', companyPhone:'', yearFounded:'', companyStructure:'', salesTaxCertNumber:'',
    contactName:'', contactRole:'', contactEmail:'', contactEmailConfirm:'', contactPhone:'', estimatedSF:'', howDidYouHear:'',
    salesTaxCertFile:null, businessLicenseFile:null, nonProfitProofFile:null,
  });

  const goToStep = (n) => { setStep(n); window.scrollTo({top:0,behavior:'smooth'}); };

  const handleSubmit = async () => {
    if (!items.length) { setError('Add at least one product to your RFQ.'); return; }
    setSubmitting(true); setError('');
    const data = new FormData();
    data.append('rfq_number', rfqNum);
    const wireForm = {...form, companyPhone:digitsOnly(form.companyPhone), contactPhone:digitsOnly(form.contactPhone)};
    ['companyName','businessType','companyAddress','companyWebsite','companyEIN','companyPhone','yearFounded','companyStructure','salesTaxCertNumber','contactName','contactRole','contactEmail','contactPhone','estimatedSF','howDidYouHear'].forEach(k => { if (wireForm[k]) data.append(k, wireForm[k]); });
    if (form.salesTaxCertFile)    data.append('salesTaxCertFile', form.salesTaxCertFile);
    if (form.businessLicenseFile) data.append('businessLicenseFile', form.businessLicenseFile);
    if (form.nonProfitProofFile)  data.append('nonProfitProofFile', form.nonProfitProofFile);
    data.append('items', JSON.stringify(items.map(i => { const d = dims[i.cartItemId]||{}; return { product_id:i.product._id, product_name:i.product.name, quantity:i.quantity, notes:i.notes||'', configuration:i.configuration||{}, dimensions:{width:d.width?parseFloat(d.width):null,length:d.length?parseFloat(d.length):null,unit:d.unit||'sqft'} }; })));
    try {
      await submitRFQ(data);
      clearCart();
      navigate('/rfq/confirmation');
    } catch(err) {
      setError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main style={{ paddingTop: 76 }}>
      <div id="refresh-top"/>

      {/* ── Page header ── */}
      <section style={{ background:'var(--ivory-soft)', padding:'48px 0' }}>
        <div className="page-container">
          <p className="eyebrow eyebrow-gold animate-fade-up" style={{ marginBottom:12 }}>Wholesale · Request for Quote</p>
          <h1 className="animate-fade-up delay-1" style={{ fontFamily:'var(--serif)', fontSize:'clamp(30px,6vw,68px)', lineHeight:1, textWrap:'balance' }}>
            Let's price your project,{' '}<em style={{ fontWeight:300, color:'var(--saffron-deep)' }}>thoughtfully.</em>
          </h1>
          <RFQSteps step={step}/>
        </div>
      </section>

      {/* ── Main content — sidebar moves BELOW form on mobile ── */}
      <div className="page-container" style={{ padding:'40px 0 96px' }}>
        <div className="rfq-layout">
          {/* Main form column */}
          <div key={step}>
            {step === 1 && (
              <div>
                <h2 style={{ fontFamily:'var(--serif)', fontSize:'clamp(22px,5vw,28px)', marginBottom:6 }}>Review Your Selections</h2>
                <p style={{ fontFamily:'var(--sans)', fontSize:14, color:'var(--ash)', marginBottom:24, lineHeight:1.6 }}>Confirm the collections, quantities, and dimensions you'd like priced. Edit items in the summary panel below.</p>
                {items.length === 0 && (
                  <div style={{ padding:40, textAlign:'center', background:'var(--ivory-soft)', borderRadius:'var(--r-md)' }}>
                    <div style={{ width:64, height:64, borderRadius:'50%', background:'var(--bone)', display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
                      <FileText size={24} style={{ color:'var(--ash)' }}/>
                    </div>
                    <h3 style={{ fontFamily:'var(--serif)', fontSize:22, marginBottom:8 }}>Your quote cart is empty</h3>
                    <p style={{ fontFamily:'var(--sans)', fontSize:13, color:'var(--ash)', marginBottom:24 }}>Add products from the catalog before submitting an RFQ.</p>
                    <a href="/catalog" className="btn btn-saffron" style={{ display:'inline-flex', alignItems:'center', gap:8 }}>Browse Collections <ArrowRight size={14}/></a>
                  </div>
                )}
                <div className="rfq-step-nav" style={{ marginTop:32 }}>
                  <a href="/catalog" className="btn btn-outline" style={{ display:'inline-flex', alignItems:'center', gap:8, textDecoration:'none' }}>
                    ← Browse More Collections
                  </a>
                  <button type="button" onClick={() => goToStep(2)} disabled={items.length === 0} className="btn btn-saffron btn-lg rfq-cta-btn" style={{ display:'flex', alignItems:'center', gap:8, opacity:items.length > 0 ? 1 : 0.4 }}>
                    Continue <ArrowRight size={14}/>
                  </button>
                </div>
              </div>
            )}
            {step === 2 && <CompanyInfoStep form={form} setForm={setForm} onBack={() => goToStep(1)} onNext={() => goToStep(3)}/>}
            {step === 3 && <DocumentsStep form={form} setForm={setForm} onBack={() => goToStep(2)} onNext={() => goToStep(4)}/>}
            {step === 4 && <ReviewStep form={form} items={items} dims={dims} submitting={submitting} error={error} onBack={() => goToStep(3)} onSubmit={handleSubmit} onPreview={() => setShowPreview(true)}/>}
          </div>

          {/* Sidebar — stacks below on mobile */}
          <RFQSidebar items={items} dims={dims} updateDim={updateDim} updateQuantity={updateQuantity} updateNotes={updateNotes} removeItem={removeItem}/>
        </div>
      </div>

      {showPreview && <InvoicePreview form={form} items={items} dims={dims} rfqNum={rfqNum} onClose={() => setShowPreview(false)}/>}
    </main>
  );
}
